'use client';

import React, { useEffect, useRef, useState } from 'react';
import styles from './CurrencyConverter.module.scss';
import CurrencySelector from '../CurrencyConverter/currencySelector/currencySelector';
import getCurrencySymbol from '../CurrencyConverter/utils/getCurrencySymbol';
import sanitiseCurrencyInput from '../CurrencyConverter/utils/sanitiseCurrencyInput';
import currency from 'currency.js';
import Cta from '@JohnLewisPartnership/jlfs-ui-library/dist/components/cta/cta';
import { MappedFresCurrency } from '../../utils/getFresCurrencies/getFresCurrencies';
import { FresQuoteResult } from '../../app/api/fres-quote/getFresQuoteData';
import { useDebounce } from '../../hooks/useDebounce/useDebounce';
import { PrimaryButton } from '@JohnLewisPartnership/jl-design-system/dist/elements/button/Button';
import { dispatchAnalyticsEvent } from '@JohnLewisPartnership/jlfs-ui-library/dist/components/analytics/Analytics';
import Message from '@JohnLewisPartnership/jlfs-ui-library/dist/components/message/message';
import { LoadingSpinner } from '@JohnLewisPartnership/jl-design-system/dist/components/loading-spinner/LoadingSpinner';
import { trackMonetateEvent } from '@JohnLewisPartnership/jlfs-ui-library/dist/utils/monetate/trackMonetateEvent';
import classNames from 'classnames';
import buildSafeFresUrl from '../../utils/buildSafeFresUrl/buildSafeFresUrl';

export type TCurrencyConverterCCProps = {
  title: string;
  ctaLinkToSectionId?: string;
  enableRateSalePageCta?: boolean;
  currencyList: Array<MappedFresCurrency>;
  rateError?: boolean;
  fresUrl: string;
  variant?: string;
  monetateTrackingId?: string;
  monetateInteractionId?: string;
  monetateViewRatesId?: string;
};

const gbpCurrency: MappedFresCurrency = {
  countryCode: 'GB',
  currencyCode: 'GBP',
  currencyName: 'Great British Pounds',
  currencySymbol: '£',
  productId: undefined,
};

const extractCurrencyValue = (
  value: string,
  symbol: string
): string | undefined => {
  const parts = value.split(symbol);
  return parts.length > 1 ? parts[1] : undefined;
};

const MAX_VALUE = 1000000000;

function CurrencyConverterCC({
  title,
  ctaLinkToSectionId = '',
  enableRateSalePageCta = false,
  currencyList,
  rateError,
  fresUrl,
  variant,
  monetateTrackingId,
  monetateInteractionId,
  monetateViewRatesId,
}: TCurrencyConverterCCProps) {
  const safeFresUrl = buildSafeFresUrl(fresUrl);

  const defaultSelectedCurrency = currencyList[0];
  const [selectedCurrency, setSelectedCurrency] = useState<MappedFresCurrency>(
    defaultSelectedCurrency
  );
  const [selectedCurrencySymbol, setSelectedCurrencySymbol] = useState<string>(
    getCurrencySymbol(selectedCurrency?.currencyCode)
  );
  const [poundsInputValue, setPoundsInputValue] = useState<string>('£');
  const [selectedCurrencyInputValue, setSelectedCurrencyInputValue] =
    useState<string>(selectedCurrencySymbol);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [quoteId, setQuoteId] = useState<string | undefined>(undefined);
  const [exchangeRate, setExchangeRate] = useState<number | undefined>(
    undefined
  );
  const [apiError, setApiError] = useState<boolean>(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const openNewTab = (e: React.MouseEvent) => {
    e.preventDefault();
    if (typeof window === 'undefined') return;

    const exchangeUrl = quoteId
      ? buildSafeFresUrl(fresUrl, quoteId)
      : safeFresUrl;

    if (!exchangeUrl) {
      return;
    }

    trackMonetateEvent(monetateTrackingId || 'FXOrderNowHeroClick');

    dispatchAnalyticsEvent('currencyConverterOrderNowClick', {
      linkUrl: exchangeUrl,
      linkText: 'Add to order',
      currency: selectedCurrency?.currencyCode || 'EUR',
      currencyType: '',
      sourceAmount: extractCurrencyValue(poundsInputValue, '£'),
      destinationAmount:
        extractCurrencyValue(
          selectedCurrencyInputValue,
          selectedCurrencySymbol
        ) || '',
    });
    window.open(exchangeUrl, '_blank', 'noopener');
  };

  useEffect(() => {
    dispatchAnalyticsEvent('fxCalculatorCTA', {
      calculatorAction: 'Currency Change',
      calculatorDetail: selectedCurrency?.currencyCode || 'EUR',
    });
  }, [selectedCurrency]);

  const callApi = async (
    productId: string,
    symbol: string,
    value: number,
    reverse: boolean,
    currencyCode: string
  ) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setIsLoading(true);
    setApiError(false);
    try {
      const response = await fetch('/api/fres-quote', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productId,
          value: Math.min(value, MAX_VALUE),
          reverse,
        }),
        signal: controller.signal,
      });
      if (!response.ok) throw new Error('Failed to fetch FRES quote');
      const result: FresQuoteResult = await response.json();
      const poundsFormatted = `£${currency(result.poundsValue).format({ symbol: '' })}`;
      const currencyFormatted = `${symbol}${currency(result.currencyValue).format({ symbol: '' })}`;
      setPoundsInputValue(poundsFormatted);
      setSelectedCurrencyInputValue(currencyFormatted);
      setQuoteId(result.quoteId);
      setExchangeRate(result.exchangeRate);
      dispatchAnalyticsEvent('currencyConverterInteraction', {
        currency: currencyCode || 'EUR',
        currencyType: '',
        sourceAmount: extractCurrencyValue(poundsFormatted, '£'),
        destinationAmount: extractCurrencyValue(currencyFormatted, symbol),
      });
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setApiError(true);
      setQuoteId(undefined);
      setExchangeRate(undefined);
    } finally {
      if (abortControllerRef.current === controller) setIsLoading(false);
    }
  };

  const { reset: debouncedApiCall, cancel: cancelDebouncedApiCall } =
    useDebounce(callApi, 600);

  const handlePoundsInputValueUpdated = (value: string): void => {
    const sanitised = sanitiseCurrencyInput('£', value);
    setPoundsInputValue(sanitised);
    setSelectedCurrencyInputValue(selectedCurrencySymbol);
    const numValue = currency(sanitised).value;
    if (numValue > 0) {
      abortControllerRef.current?.abort();
      debouncedApiCall(
        selectedCurrency.productId ?? selectedCurrency.currencyCode,
        selectedCurrencySymbol,
        numValue,
        false,
        selectedCurrency.currencyCode
      );
    } else {
      cancelDebouncedApiCall();
      abortControllerRef.current?.abort();
    }
  };

  const handleSelectedCurrencyInputValueUpdated = (value: string): void => {
    const sanitised = sanitiseCurrencyInput(selectedCurrencySymbol, value);
    setSelectedCurrencyInputValue(sanitised);
    setPoundsInputValue('£');
    const numValue = currency(
      sanitised.replace(selectedCurrencySymbol, '')
    ).value;
    if (numValue > 0) {
      abortControllerRef.current?.abort();
      debouncedApiCall(
        selectedCurrency.productId ?? selectedCurrency.currencyCode,
        selectedCurrencySymbol,
        numValue,
        true,
        selectedCurrency.currencyCode
      );
    } else {
      cancelDebouncedApiCall();
    }
  };

  const handleNewCurrencySelected = (newCurrency: MappedFresCurrency): void => {
    setSelectedCurrency(newCurrency);
    const newSymbol = getCurrencySymbol(newCurrency.currencyCode);
    setSelectedCurrencySymbol(newSymbol);
    setSelectedCurrencyInputValue(newSymbol);
    setExchangeRate(undefined);
    const poundsValue = currency(poundsInputValue).value;
    const foreignValue = currency(
      selectedCurrencyInputValue.replace(selectedCurrencySymbol, '')
    ).value;
    if (poundsValue > 0) {
      callApi(
        newCurrency!.productId! ?? newCurrency!.currencyCode!,
        newSymbol,
        poundsValue,
        false,
        newCurrency.currencyCode ?? ''
      );
    } else if (foreignValue > 0) {
      callApi(
        newCurrency!.productId! ?? newCurrency!.currencyCode!,
        newSymbol,
        foreignValue,
        true,
        newCurrency.currencyCode ?? ''
      );
    }
  };

  const handleInputFocusUpdated = (value: string): void => {
    if (value.includes('£')) {
      if (currency(value).value === 0) setPoundsInputValue('£');
    } else if (value.includes(selectedCurrencySymbol)) {
      if (currency(value).value === 0)
        setSelectedCurrencyInputValue(selectedCurrencySymbol);
    }
  };

  const loadingSpinner = isLoading ? (
    <LoadingSpinner size="medium" className={styles.loadingSpinner} />
  ) : null;

  return (
    <section>
      <div
        className={styles.currencyConverter}
        data-testid="currency-converter"
      >
        <div className={styles.topSection}>
          <h2 className={styles.title}>{title}</h2>
          <span className={styles.subheading}>
            Minimum order £250 - maximum £2,500
          </span>
        </div>
        {rateError && (
          <div className={styles.errorMessage}>
            <Message
              title="We're unable to display exchange rates at the moment."
              type="warning"
            >
              <span>
                Please try again later or view our{' '}
                <a href={safeFresUrl || '#'} target="_blank" rel="noopener">
                  order travel money page
                </a>
              </span>
            </Message>
          </div>
        )}
        <div className={styles.bottomSection}>
          {!rateError && (
            <span className={styles.inputSection}>
              <span className={styles.inputContainer}>
                <CurrencySelector
                  label="How much would you like?"
                  selectedCurrency={gbpCurrency}
                  inputValue={poundsInputValue}
                  dropdownEnabled={false}
                  inputValueUpdated={handlePoundsInputValueUpdated}
                  focusUpdated={handleInputFocusUpdated}
                  loadingSpinner={loadingSpinner}
                  monetateClickEvent={monetateInteractionId}
                ></CurrencySelector>
              </span>
              <span className={styles.separator}>=</span>
              <span className={styles.inputContainer}>
                <CurrencySelector
                  label="What you’ll get in your currency"
                  currencyList={currencyList.filter(
                    (c) => c.currencyCode !== selectedCurrency.currencyCode
                  )}
                  selectedCurrency={selectedCurrency}
                  inputValue={selectedCurrencyInputValue}
                  inputValueUpdated={handleSelectedCurrencyInputValueUpdated}
                  selectedCurrencyUpdated={handleNewCurrencySelected}
                  focusUpdated={handleInputFocusUpdated}
                  loadingSpinner={loadingSpinner}
                  monetateClickEvent={monetateInteractionId}
                ></CurrencySelector>
              </span>
            </span>
          )}
          {apiError && (
            <div className={styles.errorMessage}>
              <Message
                className={styles.content}
                title="Our currency calculator isn’t available at the moment"
                copy="You can still order now and choose your currency at the next step."
                type="warning"
                icon={null}
              />
            </div>
          )}
          {!rateError && (
            <div className={styles.onlineRate}>
              <span>Online rate: </span>
              <span className={styles.sellRate}>
                {exchangeRate !== undefined
                  ? `£1 = ${selectedCurrencySymbol}${exchangeRate}`
                  : `£1 = `}
              </span>
            </div>
          )}
          <div
            className={classNames(
              variant === 'JLMFX004'
                ? styles.ctaContainerJLMFX004
                : styles.ctaContainer
            )}
          >
            {!rateError ? (
              <Cta
                type="underlined"
                target={
                  enableRateSalePageCta
                    ? '/travel-money/rate-table'
                    : ctaLinkToSectionId.charAt(0) === '#'
                      ? ctaLinkToSectionId
                      : `#${ctaLinkToSectionId}`
                }
                label="View today's rates"
                monetateClickEvent={monetateViewRatesId}
              />
            ) : (
              <div></div>
            )}
            <PrimaryButton
              data-testid="primary-button"
              tag="a"
              href=""
              target="_blank"
              inline={true}
              rel="noopener"
              onClick={openNewTab}
              className={styles.orderNowButton}
              tabIndex={0}
            >
              Add to order
            </PrimaryButton>
          </div>
        </div>
      </div>
    </section>
  );
}

export default CurrencyConverterCC;
